/** Live numbers for one page of a read-only report, re-asked when the page or period changes. */

import { useEffect, useState } from 'react';

import type { CompareSetting, RangeSetting, WidgetResult } from '../reports/reports-api.ts';

export interface PageQuery {
  page: number;
  range: RangeSetting;
  compare?: CompareSetting;
}

export type QueryPage = (
  input: PageQuery,
) => Promise<{ results: WidgetResult[]; range: { from: string; to: string } }>;

export function useReadOnlyPage(input: {
  /** Identifies the report, so a different one is asked again. */
  reportKey: string;
  query: QueryPage;
  page: number;
  range: RangeSetting;
  compare: CompareSetting | undefined;
}) {
  const [results, setResults] = useState<Record<string, WidgetResult>>({});
  const [period, setPeriod] = useState<{ from: string; to: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const signature = JSON.stringify([input.reportKey, input.page, input.range, input.compare]);
  useEffect(() => {
    let cancelled = false;
    input
      .query({
        page: input.page,
        range: input.range,
        ...(input.compare ? { compare: input.compare } : {}),
      })
      .then((data) => {
        if (cancelled) return;
        setResults(Object.fromEntries(data.results.map((result) => [result.id, result])));
        setPeriod(data.range);
        setError(null);
      })
      .catch(
        (problem: unknown) =>
          !cancelled &&
          setError(problem instanceof Error ? problem.message : 'The numbers could not be loaded.'),
      );
    return () => {
      cancelled = true;
    };
    // The signature captures every input that changes the answer.
  }, [signature]);
  return { results, period, error };
}
